import { http } from './http'
import { useAuthStore } from '../stores/auth'

const AUTHORIZE_PREFIX = '/PlatformCoreServices/Authorize'
const TASK_PREFIX = '/PlatformCoreServices/TaskMgmt'
const MOPHR_PREFIX = '/PlatformCoreServices/MOPHR'

export async function getOrganizationById(orgId: number): Promise<string> {
  try {
    const response = await http.get<any>(
      `${AUTHORIZE_PREFIX}/OrganizationMaster/Get?id=${orgId}`
    )
    const org = Array.isArray(response?.result) ? response.result[0] : response?.result
    return org?.Name ?? org?.name ?? ''
  } catch {
    return ''
  }
}

export async function getDepartmentsByOrg(orgId: number) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/DepartmentMaster/ListGetByOrganizationId?OrganizationId=${orgId}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

interface ApiResponse<T = any> {
  result: T
  ErrorInfo: {
    ErrorCode: string | null
    ErrorMessage: string | null
    ErrorType: string | null
  }
}

// ─────────────────────────────────────────────
// CIRCULARS
// ─────────────────────────────────────────────

export async function getOrgCirculars(orgId: number, branchId: number, isAdmin: boolean = false) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/Circular/ListByOrg?orgId=${orgId}&branchId=${branchId}&isAdmin=${isAdmin}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

export async function getDeptCirculars(orgId: number, branchId: number, deptId: number, isAdmin: boolean = false) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/Circular/ListByDept?orgId=${orgId}&branchId=${branchId}&deptId=${deptId}&isAdmin=${isAdmin}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

export async function getCircularById(id: number) {
  try {
    const response = await http.get<ApiResponse>(`${AUTHORIZE_PREFIX}/Circular/Get?id=${id}`)
    const r = response?.result
    if (!r || !r.Id) return null
    return r
  } catch {
    return null
  }
}

export async function createCircular(dto: any) {
  try {
    const response = await http.post<ApiResponse>(`${AUTHORIZE_PREFIX}/Circular/Create`, dto)
    return response?.result ?? null
  } catch {
    return null
  }
}

export async function updateCircular(dto: any) {
  try {
    const response = await http.post<ApiResponse>(`${AUTHORIZE_PREFIX}/Circular/Update`, dto)
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function deleteCircular(id: number, updatedBy: number) {
  try {
    const response = await http.delete<ApiResponse>(
      `${AUTHORIZE_PREFIX}/Circular/Delete?id=${id}&updatedBy=${updatedBy}`
    )
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function activateCircular(id: number, status: boolean, updatedBy: number) {
  try {
    const response = await http.post<ApiResponse>(
      `${AUTHORIZE_PREFIX}/Circular/Active?id=${id}&status=${status}&updatedBy=${updatedBy}`
    )
    return response?.result ?? false
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────
// SOCIAL PAGE CONFIGS
// ─────────────────────────────────────────────

export async function getSocialPageConfigs(orgId: number, branchId: number) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/SocialPageConfig/ListByOrg?orgId=${orgId}&branchId=${branchId}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

// ─────────────────────────────────────────────
// SOP DOCUMENTS
// ─────────────────────────────────────────────

export async function getSOPDocuments(orgId: number, branchId: number) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/SOPDocument/List?orgId=${orgId}&branchId=${branchId}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

export async function getSOPDocumentsByCategory(orgId: number, branchId: number, category: string) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/SOPDocument/ListByCategory?orgId=${orgId}&branchId=${branchId}&category=${encodeURIComponent(category)}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

export async function searchSOPDocuments(orgId: number, branchId: number, term: string) {
  try {
    const response = await http.get<ApiResponse>(
      `${AUTHORIZE_PREFIX}/SOPDocument/Search?orgId=${orgId}&branchId=${branchId}&term=${encodeURIComponent(term)}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

export async function getSOPDocumentById(id: number) {
  try {
    const response = await http.get<ApiResponse>(`${AUTHORIZE_PREFIX}/SOPDocument/Get?id=${id}`)
    const r = response?.result
    if (!r || !r.Id) return null
    return r
  } catch {
    return null
  }
}

export async function createSOPDocument(dto: any) {
  try {
    const response = await http.post<ApiResponse>(`${AUTHORIZE_PREFIX}/SOPDocument/Create`, dto)
    return response?.result ?? null
  } catch {
    return null
  }
}

export async function updateSOPDocument(dto: any) {
  try {
    const response = await http.post<ApiResponse>(`${AUTHORIZE_PREFIX}/SOPDocument/Update`, dto)
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function activateSOPDocument(id: number, status: boolean, updatedBy: number) {
  try {
    const response = await http.post<ApiResponse>(
      `${AUTHORIZE_PREFIX}/SOPDocument/Active?id=${id}&status=${status}&updatedBy=${updatedBy}`
    )
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function deleteSOPDocument(id: number, updatedBy: number) {
  try {
    const response = await http.delete<ApiResponse>(
      `${AUTHORIZE_PREFIX}/SOPDocument/Delete?ID=${id}&updatedBy=${updatedBy}`
    )
    return response?.result ?? false
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────
// TASKS (reuses existing TaskMgmt endpoints)
// ─────────────────────────────────────────────

export async function getTaskDashboard(
  userId: number,
  orgId: number,
  branchId: number,
  isAdmin: boolean,
  isDeptHead: boolean,
  deptRoleMapId: number
) {
  try {
    const response = await http.get<ApiResponse>(
      `${TASK_PREFIX}/Dashboard/GetListByUserId?UserId=${userId}&ClientId=${orgId}&BranchId=${branchId}&IsAdmin=${isAdmin ? 1 : 0}&IsDeptHead=${isDeptHead ? 1 : 0}&DepartmentId=${deptRoleMapId}&DateType=1`
    )
    return response?.result ?? null
  } catch {
    return null
  }
}

export async function getMyTasks(
  orgId: number,
  branchId: number,
  userId: number,
  isAdmin: boolean,
  deptRoleMapId: number,
  page: number = 1,
  pageSize: number = 10
) {
  try {
    // Call TaskMgmt directly — avoids double-hop through MOPAuthorization/Common
    const response = await http.get<ApiResponse>(
      `${TASK_PREFIX}/Task/List?clientId=${orgId}&branchId=${branchId}&isAdmin=${isAdmin}&isDepartmentHead=false&userId=${userId}&DeptRoleMapId=${deptRoleMapId}&TaskTypeId=0&TaskStatusId=0&TaskPriorityId=0&DepartmentId=0&Assignee=0&SLAStatus=0&Search=&TaskCategory=1&IsContact=false&PageNumber=${page}&PageSize=${pageSize}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

export async function getDeptTasks(
  orgId: number,
  branchId: number,
  deptId: number,
  userId: number,
  isAdmin: boolean,
  deptRoleMapId: number,
  page: number = 1,
  pageSize: number = 10
) {
  try {
    // Call TaskMgmt directly — avoids double-hop through MOPAuthorization/Common
    const response = await http.get<ApiResponse>(
      `${TASK_PREFIX}/Task/List?clientId=${orgId}&branchId=${branchId}&isAdmin=${isAdmin}&isDepartmentHead=true&userId=${userId}&DeptRoleMapId=${deptRoleMapId}&TaskTypeId=0&TaskStatusId=0&TaskPriorityId=0&DepartmentId=${deptId}&Assignee=0&SLAStatus=0&Search=&TaskCategory=1&IsContact=false&PageNumber=${page}&PageSize=${pageSize}`
    )
    return response?.result ?? []
  } catch {
    return []
  }
}

// ─────────────────────────────────────────────
// BIRTHDAYS
// ─────────────────────────────────────────────

export async function getUpcomingBirthdays(organizationId: number, daysAhead: number = 30) {
  try {
    const response = await http.get<any>(
      `${MOPHR_PREFIX}/EmployeeMaster/GetUpcomingBirthdays?daysAhead=${daysAhead}&organizationId=${organizationId}`
    )
    const data = response?.result?.Data ?? response?.result ?? []
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return Array.isArray(data) ? data.map((e: any) => {
      const dob = (e.DateOfBirth ?? '').split('-') // DD-MM-YYYY
      let daysUntil = 999
      if (dob.length === 3) {
        const bday = new Date(today.getFullYear(), parseInt(dob[1]) - 1, parseInt(dob[0]))
        if (bday < today) bday.setFullYear(today.getFullYear() + 1)
        daysUntil = Math.round((bday.getTime() - today.getTime()) / 86400000)
      }
      return {
        Name: `${e.FirstName ?? ''} ${e.LastName ?? ''}`.trim(),
        DateOfBirth: e.DateOfBirth,
        DepartmentName: e.DepartmentName ?? e.Department ?? '',
        ProfilePhotoPath: e.ProfilePhotoPath ?? null,
        EmploymentStatus: e.EmploymentStatus,
        daysUntil
      }
    }).sort((a: any, b: any) => a.daysUntil - b.daysUntil) : []
  } catch {
    return []
  }
}

// ─────────────────────────────────────────────
// FCM DEVICE TOKEN
// ─────────────────────────────────────────────

export async function registerDeviceToken(dto: {
  UserId: number
  DeviceToken: string
  Platform: string
  OrganizationId: number
  BranchId: number
  CreatedBy: number
}) {
  try {
    const response = await http.post<ApiResponse>(
      `${AUTHORIZE_PREFIX}/DeviceToken/Register`,
      dto
    )
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function unregisterDeviceToken(userId: number, platform: string, updatedBy: number) {
  try {
    const response = await http.delete<ApiResponse>(
      `${AUTHORIZE_PREFIX}/DeviceToken/Unregister?userId=${userId}&platform=${platform}&updatedBy=${updatedBy}`
    )
    return response?.result ?? false
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────
// CONVENIENCE: get user context from auth store
// ─────────────────────────────────────────────

export function getDashboardUserContext() {
  const authStore = useAuthStore()
  const isAdmin = authStore.isAdmin || authStore.user?.isAdmin || false
  const isDepartmentHead = authStore.isDepartmentHead || false
  return {
    userId: authStore.user?.id ?? authStore.userId ?? 0,
    orgId: authStore.user?.organizationId ?? authStore.OrganizationId ?? 0,
    branchId: authStore.user?.branchId ?? authStore.branchId ?? 0,
    roleId: authStore.user?.roleId ?? authStore.roleId ?? 0,
    deptRoleMapId: authStore.user?.deptRoleMapId ?? authStore.deptRoleMapId ?? 0,
    isAdmin,
    isDepartmentHead,
    canManageSOP: isAdmin || isDepartmentHead,
    // departmentId: prefer user.departmentId, fallback to state.SubDepartmentId
    departmentId: authStore.user?.departmentId ?? authStore.SubDepartmentId ?? 0,
    organizationName: authStore.user?.organizationName ?? '',
    userName: authStore.user?.name ?? authStore.userName ?? ''
  }
}

// ─────────────────────────────────────────────
// NEWS & EVENTS (multi-tenant with category)
// ─────────────────────────────────────────────

export async function getNewsEventById(id: number) {
  try {
    const response = await http.get<ApiResponse>(`${AUTHORIZE_PREFIX}/NewsEvents/Get?id=${id}`)
    const r = response?.result
    if (!r || !r.Id) return null
    return r
  } catch {
    return null
  }
}

export async function createNewsEvent(dto: any) {
  try {
    const response = await http.post<ApiResponse>(`${AUTHORIZE_PREFIX}/NewsEvents/Create`, dto)
    return response?.result ?? null
  } catch {
    return null
  }
}

export async function updateNewsEvent(dto: any) {
  try {
    const response = await http.post<ApiResponse>(`${AUTHORIZE_PREFIX}/NewsEvents/Update`, dto)
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function deleteNewsEvent(id: number) {
  try {
    const response = await http.delete<ApiResponse>(`${AUTHORIZE_PREFIX}/NewsEvents/Delete?ID=${id}`)
    return response?.result ?? false
  } catch {
    return false
  }
}

export async function getNewsByOrg(orgId: number, branchId: number, roleId: number, category?: string) {
  try {
    let url = `${AUTHORIZE_PREFIX}/NewsEvents/ListByOrg?orgId=${orgId}&branchId=${branchId}&roleId=${roleId}`
    if (category && category !== 'All') url += `&category=${encodeURIComponent(category)}`
    const response = await http.get<ApiResponse>(url)
    return response?.result ?? []
  } catch {
    // Fallback to legacy ListByRoleId if new endpoint not yet deployed
    try {
      const fallback = await http.get<ApiResponse>(
        `${AUTHORIZE_PREFIX}/NewsEvents/ListByRoleId?RoleId=${roleId}`
      )
      return fallback?.result ?? []
    } catch {
      return []
    }
  }
}
